import { Box, Checkbox, HStack, Icon, Text, Tooltip, Tr } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { TbInfoCircle } from 'react-icons/tb';
import type { EnabledNotificationTypesDict } from '../../../hooks/useGetProfile';
import type { NotificationChannel, NotificationType } from '../../../hooks/useGetNotifications';
import Pagination from '../../../components/Pagination';
import {
    SettingsSubTable,
    SettingsTable,
    SettingsTableFooter,
    SettingsTableTabs,
    SettingsTd,
    SettingsTh,
    useDynamicPageSize,
    useSubTablePagination,
} from '../../../components/settings/primitives';
import { EMAIL_EXCLUDED_TYPES, type NotificationCategory } from './notificationCatalog';

export interface NotificationPrefsTableProps {
    prefs: EnabledNotificationTypesDict;
    isPaused: boolean;
    isDisabled?: boolean;
    categories: NotificationCategory[];
    variant?: 'page' | 'embedded';
    onChannelToggle: (
        types: NotificationType[],
        channel: NotificationChannel,
        isEnabled: boolean,
    ) => void;
}

const NotificationPrefsTable = ({
    prefs,
    isPaused,
    isDisabled = false,
    categories,
    variant = 'page',
    onChannelToggle,
}: NotificationPrefsTableProps) => {
    const [activeTabId, setActiveTabId] = useState(categories[0]?.header ?? '');
    useEffect(() => {
        if (!categories.some((category) => category.header === activeTabId)) {
            setActiveTabId(categories[0]?.header ?? '');
        }
    }, [categories, activeTabId]);
    const activeCategory =
        categories.find((category) => category.header === activeTabId) ?? categories[0];
    const categoryTypes = activeCategory?.types ?? [];
    // The settings page table fills leftover viewport height, so it pages by how
    // many rows fit instead of a fixed 10. The embedded user-detail card is only
    // as tall as its rows, so it shows the whole category.
    const { scrollRef, pageSize: viewportPageSize } = useDynamicPageSize();
    const pageSize =
        variant === 'page' ? viewportPageSize : Math.max(categoryTypes.length, 1);
    const {
        currentPage,
        setCurrentPage,
        totalPages,
        pageItems,
    } = useSubTablePagination(categoryTypes, pageSize);
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTabId, setCurrentPage]);
    const controlsDisabled = isPaused || isDisabled;

    if (!activeCategory) {
        return (
            <Text fontSize="sm" color="gray.500" px={variant === 'embedded' ? 0 : 8} py={4}>
                This user has no notification types for their role.
            </Text>
        );
    }

    const columnTypes = (channel: NotificationChannel) =>
        activeCategory.types
            .filter(({ type }) => channel !== 'email' || !EMAIL_EXCLUDED_TYPES.includes(type))
            .map(({ type }) => type);

    const renderHeaderCheckbox = (channel: NotificationChannel, columnLabel: string) => {
        const types = columnTypes(channel);
        const allChecked =
            types.length > 0 && types.every((type) => (prefs[type] ?? []).includes(channel));
        const someChecked = types.some((type) => (prefs[type] ?? []).includes(channel));
        return (
            <Checkbox
                size="sm"
                colorScheme="partbay-blue"
                isChecked={isPaused ? false : allChecked}
                isIndeterminate={!isPaused && !allChecked && someChecked}
                isDisabled={controlsDisabled}
                onChange={(e) => onChannelToggle(types, channel, e.target.checked)}
                aria-label={`Toggle ${columnLabel} for all ${activeCategory.header} events`}
            />
        );
    };

    const tabs = (
        <SettingsTableTabs
            tabs={categories.map((category) => ({
                id: category.header,
                label: category.header,
                count: category.types.length,
            }))}
            value={activeCategory.header}
            onChange={setActiveTabId}
            px={variant === 'embedded' ? 0 : undefined}
        />
    );

    const head = (
        <>
            <SettingsTh minW="280px">Event</SettingsTh>
            <SettingsTh textAlign="center" w="120px" whiteSpace="nowrap">
                <HStack spacing={1.5} justify="center">
                    {renderHeaderCheckbox('notif', 'in-app')}
                    <Box as="span">In-App</Box>
                </HStack>
            </SettingsTh>
            <SettingsTh textAlign="center" w="120px" whiteSpace="nowrap">
                <HStack spacing={1.5} justify="center">
                    {renderHeaderCheckbox('email', 'email')}
                    <Box as="span">Email</Box>
                </HStack>
            </SettingsTh>
        </>
    );

    const rows = (
        <>
            {pageItems.map(({ type, label, description, tooltip }) => {
                const allHaveChannel = (channel: NotificationChannel) =>
                    (prefs[type] ?? []).includes(channel);
                return (
                    <Tr key={type} _hover={{ bg: 'gray.50' }}>
                        <SettingsTd>
                            <HStack spacing={1.5}>
                                <Text fontWeight="medium" color="gray.800">
                                    {label}
                                </Text>
                                {tooltip && (
                                    <Tooltip label={tooltip} hasArrow placement="top">
                                        <Box as="span" display="inline-flex" color="gray.400">
                                            <Icon as={TbInfoCircle as React.ElementType} boxSize={3.5} />
                                        </Box>
                                    </Tooltip>
                                )}
                            </HStack>
                            {description && (
                                <Text fontSize="xs" color="gray.500" mt={0.5}>
                                    {description}
                                </Text>
                            )}
                        </SettingsTd>
                        <SettingsTd textAlign="center">
                            <Checkbox
                                colorScheme="partbay-blue"
                                isChecked={isPaused ? false : allHaveChannel('notif')}
                                isDisabled={controlsDisabled}
                                onChange={(e) => onChannelToggle([type], 'notif', e.target.checked)}
                                aria-label={`${label} — in-app`}
                            />
                        </SettingsTd>
                        <SettingsTd textAlign="center">
                            {EMAIL_EXCLUDED_TYPES.includes(type) ? (
                                <Text fontSize="xs" color="gray.400">
                                    NA
                                </Text>
                            ) : (
                                <Checkbox
                                    colorScheme="partbay-blue"
                                    isChecked={isPaused ? false : allHaveChannel('email')}
                                    isDisabled={controlsDisabled}
                                    onChange={(e) => onChannelToggle([type], 'email', e.target.checked)}
                                    aria-label={`${label} — email`}
                                />
                            )}
                        </SettingsTd>
                    </Tr>
                );
            })}
        </>
    );

    if (variant === 'embedded') {
        return (
            <>
                {tabs}
                <SettingsSubTable
                    head={head}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                >
                    {rows}
                </SettingsSubTable>
            </>
        );
    }

    return (
        <>
            {tabs}
            <SettingsTable head={head} scrollRef={scrollRef}>
                {rows}
            </SettingsTable>
            {totalPages > 1 && (
                <SettingsTableFooter>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        py={0}
                    />
                </SettingsTableFooter>
            )}
        </>
    );
};

export default NotificationPrefsTable;
